import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useParams, Outlet } from 'react-router-dom';
import StaticSiteConfig from '../components/StaticSiteConfig';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  LocationOn,
  MyLocation,
  ZoomIn,
  ZoomOut,
  Fullscreen,
} from '@mui/icons-material';
import MachineNavigationBar from '../components/MachineNavigationBar';
import { gpsService, machinesService } from '../services/api';
import { GPSData } from '../types';
import { getRelativeTime } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { theme } from '../theme';

// Mapbox access token - should be stored in environment variable
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Validate the access token format
const isValidMapboxToken = (token: string | undefined): boolean => {
  return !!(token && token.startsWith('pk.') && token.length > 50);
};

// Set access token only if valid
if (MAPBOX_ACCESS_TOKEN && isValidMapboxToken(MAPBOX_ACCESS_TOKEN)) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
} else {
  console.warn('Invalid or missing Mapbox access token. Map functionality will be limited.');
}

// Custom marker component for machines
const MachineMarker: React.FC = () => {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: theme.palette.secondary.dark,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
    >
      <img 
        src={process.env.PUBLIC_URL + "/DiggerIcon.svg"} 
        alt="Machine" 
        style={{ 
          width: '24px', 
          height: '24px',
          filter: 'brightness(0) invert(1)' // Makes the icon white
        }}
      />
    </div>
  );
};

// Machine unit type for component usage
interface MachineUnit {
  unit_id: string;
  unit_name: string;
  vendor: string;
  model: string;
  availability: boolean;
  Type: string;
  max_power: number | null;
  voltage: number | null;
  alert: boolean;
}

// Memoized static content component to prevent re-renders
const MachineStaticContent = React.memo<{
  machineDetail: MachineUnit;
  user: any;
  orgId: string | undefined;
  mapContainer: React.RefObject<HTMLDivElement>;
  map: React.RefObject<mapboxgl.Map | null>;
  mapError: string | null;
  mapInitializationTimeout: boolean;
  hasValidToken: boolean;
  loadingGPS: boolean;
  gpsError: string | null;
  gpsData: GPSData[];
  gpsTimestamp: string | null;
  mapControls: Array<{ icon: React.ReactNode; action: string }>;
  handleMapControl: (action: string) => void;
  addMarkersToMap: () => void;
  onMapRetry: () => void;
}>(({ 
  machineDetail, 
  user, 
  orgId,
  mapContainer, 
  map, 
  mapError, 
  mapInitializationTimeout, 
  hasValidToken, 
  loadingGPS, 
  gpsError, 
  gpsData, 
  gpsTimestamp, 
  mapControls, 
  handleMapControl,
  addMarkersToMap,
  onMapRetry
}) => {
  return (
    <>
      {/* Machine header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 16 }} />
          <Box sx={{ position: 'relative' }}>
            <img 
              src={process.env.PUBLIC_URL + "/DiggerIcon.svg"} 
              alt="Machine" 
              style={{ width: '32px', height: '32px' }}
            />
          </Box>
        </Box>
        
        <Box>
          <Typography variant="h4" sx={{ 
            fontSize: '18px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            color: 'rgba(0,0,0,0.87)',
            lineHeight: '27px',
            marginBottom: 0
          }}>
            {machineDetail.unit_name || 'N/A'}
          </Typography>
          <Typography sx={{
            color: 'rgba(0, 0, 0, 0.60)',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '157%',
            letterSpacing: '0.1px',
          }}>
            {[machineDetail.vendor, machineDetail.model].filter(Boolean).join(' ')}
          </Typography>
        </Box>
      </Box>

      {/* Flow diagram and map */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Flow diagram */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            height: 397,
            borderRadius: 1,
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <StaticSiteConfig 
                deviceType="excavator"
                deviceId={machineDetail.unit_id}
                deviceName={machineDetail.unit_name}
                modelName={machineDetail.model}
                orgId={orgId}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Map card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            height: 397,
            borderRadius: 1,
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Map container */}
            <Box sx={{ 
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div 
                ref={mapContainer} 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '320px',
                  position: 'relative',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                }} 
              />
              
              {/* Fallback map placeholder if initialization fails */}
              {!map.current && (mapError || mapInitializationTimeout) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                    textAlign: 'center',
                    padding: 2,
                  }}
                >
                  <LocationOn sx={{ fontSize: 48, color: 'rgba(0,0,0,0.3)' }} />
                  <Typography variant="body2" sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>
                    Map unavailable
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.4)' }}>
                    {mapError || 'Initialization failed'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onMapRetry}
                    sx={{ mt: 1, fontSize: '12px' }}
                  >
                    Retry Map
                  </Button>
                </Box>
              )}

              {/* Error Display */}
              {mapError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity={hasValidToken ? "error" : "warning"}
                    sx={{ fontSize: '12px' }}
                  >
                    {mapError}
                  </Alert>
                </Box>
              )}

              {/* GPS Error Display */}
              {gpsError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="error"
                    sx={{ fontSize: '12px' }}
                  >
                    GPS Position Unavailable: {gpsError}
                  </Alert>
                </Box>
              )}

              {/* No GPS Data Available */}
              {!loadingGPS && !gpsError && gpsData.length === 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="warning"
                    sx={{ fontSize: '12px' }}
                  >
                    No GPS position data available for this machine
                  </Alert>
                </Box>
              )}


              {/* Custom Map Controls */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {mapControls.map((control, index) => (
                  <IconButton
                    key={index}
                    onClick={() => handleMapControl(control.action)}
                    size="small"
                    sx={{
                      bgcolor: 'white',
                      boxShadow: 2,
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    {control.icon}
                  </IconButton>
                ))}
              </Box>

              {/* Location info overlay */}
              <Box sx={{ 
                position: 'absolute', 
                bottom: 8, 
                right: 8, 
                display: 'flex', 
                gap: 1,
              }}>
                <Tooltip 
                  title={gpsTimestamp ? new Date(gpsTimestamp).toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  }) : "GPS timestamp not available"}
                  arrow
                >
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 16, color: 'white !important' }} />}
                    label={gpsTimestamp ? `${getRelativeTime(gpsTimestamp)} (GPS)` : "Loading GPS..."}
                    size="small"
                    sx={{
                      backgroundColor: (theme) => theme.palette.secondary.dark,
                      color: 'white',
                      fontSize: '12px',
                      fontFamily: 'Montserrat',
                      height: 24,
                      '& .MuiChip-label': {
                        fontSize: '12px',
                        lineHeight: '16px',
                        letterSpacing: '0.16px',
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
});

const MachineDetails: React.FC = () => {
  const { unitId } = useParams<{ unitId: string }>();
  const { setMachineBreadcrumbs } = useBreadcrumbs();
  const { user, isLoading: authLoading } = useAuth();
  const orgId = user?.orgId;
  const [machineDetail, setMachineDetail] = useState<MachineUnit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Map-related state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [hasValidToken] = useState(isValidMapboxToken(MAPBOX_ACCESS_TOKEN));
  const [gpsData, setGpsData] = useState<GPSData[]>([]);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsTimestamp, setGpsTimestamp] = useState<string | null>(null);
  const [mapInitializationTimeout, setMapInitializationTimeout] = useState(false);
  
  // Map controls configuration
  const mapControls = useMemo(() => [
    { icon: <ZoomIn />, action: 'zoomIn' },
    { icon: <ZoomOut />, action: 'zoomOut' },
    { icon: <MyLocation />, action: 'location' },
    { icon: <Fullscreen />, action: 'fullscreen' },
  ], []);

  // Function to fetch GPS data from API
  const fetchGPSData = useCallback(async () => {
    if (!unitId || !orgId) {
      setGpsError('Missing required information to fetch GPS data');
      setLoadingGPS(false);
      return;
    }
    
    setLoadingGPS(true);
    setGpsError(null);
    
    try {
      const response = await gpsService.getMachineGPS(unitId, orgId);
      
      if (response.success && response.data && response.data.data && response.data.data.length > 0) {
        // Get the latest entry (last item in the array)
        const gpsDataArray = response.data.data;
        const latestGPS = gpsDataArray[gpsDataArray.length - 1];
        
        // Create GPSData object with additional metadata
        const gpsDataPoint: GPSData = {
          latitude: latestGPS.latitude,
          longitude: latestGPS.longitude,
          timestamp: latestGPS.timestamp,
          unit_id: unitId,
          unit_name: machineDetail?.unit_name || `Machine ${unitId}`,
          org_id: orgId,
        };
        
        setGpsData([gpsDataPoint]);
        setGpsTimestamp(gpsDataPoint.timestamp);
        
        // Center map on the GPS position
        if (map.current) {
          if (map.current.loaded()) {
            map.current.flyTo({
              center: [gpsDataPoint.longitude, gpsDataPoint.latitude],
              zoom: 12
            });
          } else {
            map.current.once('load', () => {
              if (map.current) {
                map.current.flyTo({
                  center: [gpsDataPoint.longitude, gpsDataPoint.latitude],
                  zoom: 12
                });
              }
            });
          }
        }
      } else {
        setGpsError('No GPS data available for this machine');
        setGpsData([]);
        setGpsTimestamp(null);
      }
    } catch (error) {
      console.error('Error fetching GPS data:', error);
      setGpsError(error instanceof Error ? error.message : 'Failed to fetch GPS data');
      setGpsData([]);
      setGpsTimestamp(null);
    } finally {
      setLoadingGPS(false);
    }
  }, [unitId, orgId, machineDetail]);

  // Function to add markers to the map
  const addMarkersToMap = useCallback(() => {
    if (!map.current || !gpsData || gpsData.length === 0) {
      return;
    }

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers
    gpsData.forEach((location, index) => {
      const el = document.createElement('div');
      const root = createRoot(el);
      root.render(<MachineMarker />);
      
      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.longitude, location.latitude])
        .addTo(map.current!);

      const unitName = machineDetail?.unit_name || location.unit_name || `Machine ${index + 1}`;

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: true,
        maxWidth: '320px'
      }).setHTML(`
        <div style="
          box-sizing: border-box;
          min-width: 240px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0;
            width: 100%;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 16px; font-weight: 600; color: #666666;">
                ${unitName}
              </span>
            </div>
          </div>
          <div style="
            width: 100%;
            height: 1px;
            background-color: #B5AFA9;
            margin: 12px 0;
          "></div>
          <div style="
            display: flex;
            flex-direction: row;
            gap: 4px;
            font-size: 12px;
            font-family: 'Montserrat', sans-serif;
            color: rgba(0, 0, 0, 0.87);
          ">
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lat:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.latitude.toFixed(6)}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lng:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.longitude.toFixed(6)}</span>
            </div>
          </div>
        </div>
      `);

      marker.setPopup(popup);
      markers.current.push(marker);
    });
    
    if (markers.current.length > 0) {
      setLoadingGPS(false);
    }
  }, [gpsData, machineDetail]);

  const handleMapControl = useCallback((action: string) => {
    if (!map.current) return;

    switch (action) {
      case 'zoomIn':
        map.current.zoomIn();
        break;
      case 'zoomOut':
        map.current.zoomOut();
        break;
      case 'location':
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { longitude, latitude } = position.coords;
              map.current!.flyTo({
                center: [longitude, latitude],
                zoom: 14
              });
            },
            (error) => {
              console.error('Error getting location:', error);
            }
          );
        }
        break;
      case 'fullscreen':
        if (mapContainer.current) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            mapContainer.current.requestFullscreen();
          }
        }
        break;
    }
  }, []);

  const handleMapRetry = useCallback(() => {
    setMapError(null);
    setMapInitializationTimeout(false);
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    setTimeout(() => {
      const container = mapContainer.current;
      if (container && hasValidToken) {
        let retryCenter: [number, number] = [0, 0];
        let retryZoom = 12;
        
        if (gpsData && gpsData.length > 0) {
          const firstLocation = gpsData[0];
          retryCenter = [firstLocation.longitude, firstLocation.latitude];
        }
        
        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: retryCenter,
          zoom: retryZoom,
          attributionControl: false
        });
        map.current.on('load', () => {
          setMapError(null);
          setMapInitializationTimeout(false);
        });
      }
    }, 100);
  }, [hasValidToken, gpsData]);

  // Fetch machine detail from API
  useEffect(() => {
    const fetchMachineDetail = async () => {
      if (!unitId) {
        setLoading(false);
        setError('Machine ID not found');
        return;
      }

      if (!orgId) {
        setLoading(false);
        setError('Organization ID not found');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await machinesService.getMachines(orgId);
        if (response.success && response.data) {
          // Find the machine with matching unit_id
          const machine = response.data.machines.find(m => m.unit_id === unitId);
          
          if (machine) {
            // Map API response to component's expected format
            const mappedMachine: MachineUnit = {
              unit_id: machine.unit_id,
              unit_name: machine.unit_name,
              model: machine.model,
              vendor: machine.vendor,
              availability: machine.availability,
              Type: machine.type.charAt(0).toUpperCase() + machine.type.slice(1), // Capitalize first letter
              max_power: machine.max_power,
              voltage: machine.voltage,
              alert: machine.alert,
            };
            setMachineDetail(mappedMachine);
          } else {
            setError(`Machine with ID ${unitId} not found`);
          }
        } else {
          setError('Failed to fetch machine details');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching machine details:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch machine details');
        setLoading(false);
      }
    };

    fetchMachineDetail();
  }, [unitId, orgId]);

  // Set breadcrumbs when machine detail is loaded
  useEffect(() => {
    if (machineDetail && machineDetail.unit_name) {
      setMachineBreadcrumbs(machineDetail.unit_name);
    }
  }, [machineDetail, setMachineBreadcrumbs]);

  // Fetch GPS data
  useEffect(() => {
    if (authLoading || !unitId || !orgId) {
      return;
    }
    
    fetchGPSData();
  }, [unitId, orgId, fetchGPSData, authLoading]);

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current || !hasValidToken || !machineDetail || loading) {
      return;
    }

    if ((!gpsData || gpsData.length === 0) && !gpsError && loadingGPS) {
      return;
    }

    const initializeMap = () => {
      try {
        const container = mapContainer.current!;
        
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          setTimeout(initializeMap, 500);
          return;
        }

        if (container.offsetHeight < 200) {
          container.style.height = '320px';
        }

        let initialCenter: [number, number];
        let initialZoom = 12;
        
        if (gpsData && gpsData.length > 0) {
          const firstLocation = gpsData[0];
          initialCenter = [firstLocation.longitude, firstLocation.latitude];
        } else if (gpsError) {
          initialCenter = [0, 0];
        } else {
          initialCenter = [0, 0];
        }
        
        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: initialCenter,
          zoom: initialZoom,
          attributionControl: false
        });

        map.current.on('load', () => {
          setMapError(null);
          setMapInitializationTimeout(false);
          
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
              
              if (gpsData && gpsData.length > 0) {
                addMarkersToMap();
              }
            }
          }, 100);
        });

        map.current.on('error', (e) => {
          console.error('Map error event:', e);
          if (e.error && e.error.message) {
            setMapError(`Map error: ${e.error.message}`);
          } else {
            setMapError('An error occurred while loading the map');
          }
        });
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError(`Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    const timer = setTimeout(initializeMap, 1000);
    
    const timeoutTimer = setTimeout(() => {
      if (!map.current) {
        setMapInitializationTimeout(true);
        setMapError('Map initialization timed out. Please refresh the page.');
      }
    }, 15000);

    return () => {
      clearTimeout(timer);
      clearTimeout(timeoutTimer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [hasValidToken, machineDetail, loading, gpsData, gpsError, loadingGPS, addMarkersToMap]);

  // Effect to add markers when GPS data changes
  useEffect(() => {
    if (map.current && gpsData && gpsData.length > 0) {
      addMarkersToMap();
    }
  }, [addMarkersToMap, gpsData, map]);

  // Effect to resize map when component becomes visible
  useEffect(() => {
    if (map.current && !loading && machineDetail) {
      const timer = setTimeout(() => {
        if (map.current) {
          map.current.resize();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [loading, machineDetail]);

  const hasMatchingMachineData = machineDetail && machineDetail.unit_id === unitId;
  
  if (!hasMatchingMachineData) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading machine details...</Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography>Machine {unitId} could not be loaded</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Machine {unitId} not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: (theme) => theme.palette.background.default, 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{ flex: 1, pr: 3, pl: 3, pb: 3 }}>
        <MachineStaticContent
          machineDetail={machineDetail!}
          user={user}
          orgId={orgId}
          mapContainer={mapContainer}
          map={map}
          mapError={mapError}
          mapInitializationTimeout={mapInitializationTimeout}
          hasValidToken={hasValidToken}
          loadingGPS={loadingGPS}
          gpsError={gpsError}
          gpsData={gpsData}
          gpsTimestamp={gpsTimestamp}
          mapControls={mapControls}
          handleMapControl={handleMapControl}
          addMarkersToMap={addMarkersToMap}
          onMapRetry={handleMapRetry}
        />

        {/* Machine Navigation Bar */}
        <Box sx={{ 
          ml: -3,
          mr: -3,
          mb: 3,
        }}>
          <MachineNavigationBar />
        </Box>

        {/* Content Area - Renders nested routes */}
        <Box sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MachineDetails;

